import React, { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import PageHeader from '../../components/DashboardComponents/PageHeader';
import DashboardCard from '../../components/DashboardComponents/DashboardCard';
import { AvatarGroup } from '../../components/DashboardComponents/AvatarGroup';
import ProgressBar from '../../components/DashboardComponents/ProgressBar';
import CustomButton from '../../components/OnboardingComponents/CustomButton';
import { AddCollaboratorModal } from '../../components/Modals';
import { mapUserToCollabOption } from '../../components/DashboardComponents/CollaboratorSearchSelect';
import { useCreateFlagModal } from '../../components/Modals/CreateFlagModal';
import EmptyState from '../../components/Common/EmptyState';
import { ICONS } from '../../constants/icons';
import { getUserAvatarUrl } from '../../helpers/userAvatar';
import {
  fetchProfileProjectSections,
  mapOngoingRole,
} from './profileProjects';
import { fetchExistingMemberProfileFlag } from '../Flags/flagData';
import { fetchClassSummary, fetchSpellbookSummary, genomeConicGradient } from './profileGenome';
import {
  fetchUserProfile,
  formatCityName,
  getAddressByType,
  getProfileCurrentRoles,
  getProfileDisplayName,
  getProfileRoleLabel,
  getProfileXp,
} from './profileUser';
import {
  OngoingRoleSkeletonList,
  ProfileProjectSkeletonList,
} from './ProfileProjectSkeleton';
import ProfilePhoto from './ProfilePhoto';

const PROJECT_SECTIONS = [
  { key: 'past', title: 'Past Projects' },
  { key: 'present', title: 'Present Projects' },
  { key: 'future', title: 'Future Projects' },
];

const genreSlug = (genre = '') =>
  String(genre || 'other')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '') || 'other';

const GenomeLegend = ({ items, emptyTitle, emptyDescription }) => {
  const total = items.reduce((sum, item) => sum + Number(item.value || 0), 0);
  if (!items.length) {
    return (
      <EmptyState
        size="sm"
        title={emptyTitle}
        description={emptyDescription}
      />
    );
  }
  return (
    <ul className="my-profile__genome-legend">
      {items.map((item) => (
        <li key={item.id || item.label}>
          <span
            className="my-profile__genome-dot"
            style={{ background: item.color }}
            aria-hidden="true"
          />
          <span className="my-profile__genome-label">{item.label}</span>
          <span className="my-profile__genome-value">{Math.round(item.value)}%</span>
        </li>
      ))}
      <li className="my-profile__genome-total">
        <span className="my-profile__genome-label">Total</span>
        <span className="my-profile__genome-value">{Math.round(total)}%</span>
      </li>
    </ul>
  );
};

const GenrePill = ({ genre }) => (
  <span className={`my-profile__genre my-profile__genre--${genreSlug(genre)}`}>
    <span className="my-profile__genre-dot" aria-hidden="true" />
    {genre}
  </span>
);

const ProjectCard = ({ project }) => (
  <article className="my-profile__project-card my-profile__project-card--public">
    <div className="my-profile__project-main">
      <span className={`my-profile__code my-profile__code--${genreSlug(project.genre)}`}>
        {project.code}
      </span>
      <div className="my-profile__project-info">
        <div className="my-profile__project-title-row">
          <b className="my-profile__project-title">{project.title}</b>
          <GenrePill genre={project.genre} />
        </div>
        <p className="my-profile__project-meta">
          <span>{project.role || '—'}</span>
          <span className="my-profile__dot" aria-hidden="true">
            •
          </span>
          <span>{project.date || '—'}</span>
        </p>
      </div>
    </div>

    <div className="my-profile__project-avatars">
      <AvatarGroup
        people={project.collaborators || []}
        max={3}
        size="xs"
        total={project.totalCollaborators}
      />
    </div>
  </article>
);

/**
 * Public Profile — same app shell as My Profile; public-only content.
 * Route: /public/profile/:profileId (auth can be decided later).
 */
const PublicProfile = () => {
  const { profileId } = useParams();
  const currentUser = useSelector(state => state.auth.currentUser);
  const { openCreateFlag } = useCreateFlagModal();
  const [isInviteOpen, setIsInviteOpen] = useState(false);
  const [sectionProjects, setSectionProjects] = useState({
    past: [],
    present: [],
    future: [],
  });
  const [isLoadingProjects, setIsLoadingProjects] = useState(true);
  const [artsClass, setArtsClass] = useState([]);
  const [artsSpellbook, setArtsSpellbook] = useState([]);
  const [profile, setProfile] = useState(null);
  const [isLoadingProfile, setIsLoadingProfile] = useState(true);
  const [hasReportedUser, setHasReportedUser] = useState(false);

  useEffect(() => {
    document.title = 'Public Profile | AMOK . EARTH';
  }, []);

  useEffect(() => {
    if (!profileId) return undefined;
    let cancelled = false;

    const loadProjects = async () => {
      setIsLoadingProjects(true);
      try {
        // Future projects are private: only collaborators (incl. owner) see them.
        const grouped = await fetchProfileProjectSections({
          userId: profileId,
          viewerUserId: currentUser?.id ?? null,
          restrictFutureToViewerCollaborator: true,
        });
        if (!cancelled) {
          setSectionProjects(grouped);
        }
      } finally {
        if (!cancelled) setIsLoadingProjects(false);
      }
    };

    loadProjects();
    return () => {
      cancelled = true;
    };
  }, [profileId, currentUser?.id]);

  useEffect(() => {
    if (!profileId) return undefined;
    let cancelled = false;

    const loadGenome = async () => {
      const [classSummary, spellbookSummary] = await Promise.all([
        fetchClassSummary(profileId),
        fetchSpellbookSummary(profileId),
      ]);
      if (!cancelled) {
        setArtsClass(classSummary);
        setArtsSpellbook(spellbookSummary);
      }
    };

    loadGenome();
    return () => {
      cancelled = true;
    };
  }, [profileId]);

  useEffect(() => {
    if (!profileId) return undefined;
    let cancelled = false;

    const loadProfile = async () => {
      setIsLoadingProfile(true);
      const nextProfile = await fetchUserProfile(profileId);
      if (!cancelled) {
        setProfile(nextProfile);
        setIsLoadingProfile(false);
      }
    };

    loadProfile();
    return () => {
      cancelled = true;
    };
  }, [profileId]);

  useEffect(() => {
    if (!profileId) {
      setHasReportedUser(false);
      return undefined;
    }
    if (currentUser?.id && String(currentUser.id) === String(profileId)) {
      setHasReportedUser(false);
      return undefined;
    }

    let cancelled = false;
    setHasReportedUser(false);

    fetchExistingMemberProfileFlag(profileId)
      .then(flag => {
        if (!cancelled) setHasReportedUser(Boolean(flag));
      })
      .catch(() => {
        if (!cancelled) setHasReportedUser(false);
      });

    return () => {
      cancelled = true;
    };
  }, [profileId, currentUser?.id]);

  const displayName = getProfileDisplayName(profile, { fallback: '' });
  const avatarSrc = getUserAvatarUrl(profile);
  const roleLabel = getProfileRoleLabel(profile);
  const primaryCity = formatCityName(
    getAddressByType(profile?.addresses || [], 'primary')
  );
  const { xpValue, xpLevel, xpMax } = getProfileXp(profile);
  const listeningTo = String(profile?.listening_to || '').trim();
  const reading = String(profile?.reading || '').trim();
  const bioSubtitle = profile?.bio || roleLabel;
  const ongoingRoles = getProfileCurrentRoles(profile).map(mapOngoingRole);
  const isOwnPublicProfile =
    Boolean(currentUser?.id) &&
    [profileId, profile?.id].some(
      id => id && String(id) === String(currentUser.id)
    );
  const inviteExcludeProjectIds = useMemo(
    () =>
      PROJECT_SECTIONS.flatMap(section =>
        (sectionProjects[section.key] || []).map(project => project.id)
      ).filter(Boolean),
    [sectionProjects]
  );
  const invitePresetCollaborators = useMemo(() => {
    const invitee = profile?.id
      ? profile
      : profileId
        ? { id: profileId, full_name: displayName }
        : null;
    if (!invitee?.id && !invitee?.email) return [];
    return [mapUserToCollabOption(invitee)];
  }, [profile, profileId, displayName]);

  return (
    <div className="page-content">
      <div className="my-profile my-profile--public">
        <PageHeader
          className="my-profile__header"
          title="Public Profile"
          subtitle="See how this member shows up to the community."
          actions={
            isOwnPublicProfile ? null : (
              <>
                <CustomButton
                  variant={hasReportedUser ? 'secondary' : 'danger'}
                  size="md"
                  type="button"
                  trailingIconSrc={ICONS.profileFlagUser}
                  trailingIconAlt=""
                  className="my-profile__public-action my-profile__public-action--flag"
                  disabled={hasReportedUser}
                  title={
                    hasReportedUser
                      ? 'You have already reported this member'
                      : 'Flag this member'
                  }
                  onClick={() => {
                    if (hasReportedUser) return;
                    openCreateFlag(
                      {
                        name: displayName,
                        avatar: avatarSrc,
                        userId: profileId,
                        moduleKey: 'member_profile',
                        moduleLabel: 'Member Profile',
                      },
                      null,
                      {
                        navigateOnSuccess: false,
                        onSuccess: () => setHasReportedUser(true),
                      },
                    );
                  }}
                >
                  {hasReportedUser ? 'Already Reported' : 'Flag User'}
                </CustomButton>
                <CustomButton
                  variant="secondary"
                  size="md"
                  type="button"
                  trailingIconSrc={ICONS.profileInviteSend}
                  trailingIconAlt=""
                  className="my-profile__public-action my-profile__public-action--invite"
                  onClick={() => setIsInviteOpen(true)}
                >
                  Invite to Project
                </CustomButton>
              </>
            )
          }
        />

        <div className="my-profile__grid-row my-profile__grid-row--3">
          <div className="my-profile__card my-profile__card--bio">
            <ProfilePhoto
              src={avatarSrc}
              name={displayName}
              loading={isLoadingProfile && !avatarSrc}
              className="my-profile__avatar"
            />
            <div className="my-profile__bio-details">
              <div className="my-profile__name-row">
                <h2 className="my-profile__name">
                  {displayName || (isLoadingProfile ? '' : '—')}
                </h2>
                {roleLabel ? (
                  <span className="my-profile__badge">{roleLabel}</span>
                ) : null}
              </div>
              <p className="my-profile__meta">
                <span>{isLoadingProfile ? '' : primaryCity}</span>
                {bioSubtitle ? (
                  <>
                    <span className="my-profile__meta-sep">-</span>
                    <span>{bioSubtitle}</span>
                  </>
                ) : null}
              </p>
            </div>
          </div>

          <div className="my-profile__card my-profile__card--spellbook">
            <div className="my-profile__card-head">
              <span className="my-profile__card-title">Arts Genome - Spellbook</span>
              {artsSpellbook.length ? (
                <span
                  className="my-profile__donut"
                  style={{
                    background: genomeConicGradient(artsSpellbook),
                  }}
                  aria-hidden="true"
                />
              ) : null}
            </div>
            <GenomeLegend
              items={artsSpellbook}
              emptyTitle="No Spellbook Data Yet"
              emptyDescription="Arts genome spellbook will show up here."
            />
          </div>

          <div className="my-profile__card my-profile__card--class">
            <div className="my-profile__card-head">
              <span className="my-profile__card-title">Arts Genome - Class</span>
              {artsClass.length ? (
                <span
                  className="my-profile__donut"
                  style={{
                    background: genomeConicGradient(artsClass),
                  }}
                  aria-hidden="true"
                />
              ) : null}
            </div>
            <GenomeLegend
              items={artsClass}
              emptyTitle="No Class Data Yet"
              emptyDescription="Arts genome class will show up here."
            />
          </div>
        </div>

        <div className="my-profile__grid-row my-profile__grid-row--3">
          <DashboardCard
            title="XP's"
            tone="purple-custom"
            topRight={profile ? `Level ${xpLevel}` : ''}
          >
            <div className="my-profile__xp-body">
              <div className="my-profile__xp-row">
                <span className="my-profile__xp-value">
                  {profile ? `${xpValue}xp` : ''}
                </span>
                <span className="my-profile__xp-max">
                  {profile ? `${xpMax}xp` : ''}
                </span>
              </div>
              {profile ? (
                <ProgressBar
                  value={xpValue}
                  max={xpMax}
                  tone="purple"
                  className="my-profile__xp-bar"
                />
              ) : null}
            </div>
          </DashboardCard>

          <div className="my-profile__card my-profile__card--listening">
            <div className="my-profile__media-head">
              <img src={ICONS.profileHeadphone} alt="" width={20} height={20} />
              <span className="my-profile__card-title">Listening to...</span>
            </div>
            {listeningTo ? (
              <p className="my-profile__media-value">{listeningTo}</p>
            ) : (
              <EmptyState
                size="sm"
                title="Nothing Here Yet"
                description="What they're listening to will show up here."
              />
            )}
          </div>

          <div className="my-profile__card my-profile__card--reading">
            <div className="my-profile__media-head">
              <img src={ICONS.profileBook} alt="" width={20} height={20} />
              <span className="my-profile__card-title">Reading...</span>
            </div>
            {reading ? (
              <p className="my-profile__media-value">{reading}</p>
            ) : (
              <EmptyState
                size="sm"
                title="Nothing Here Yet"
                description="What they're reading will show up here."
              />
            )}
          </div>
        </div>

        {isLoadingProfile ? (
          <section className="my-profile__block">
            <div className="my-profile__block-head">
              <h3 className="my-profile__block-title">Ongoing Role</h3>
            </div>
            <div className="my-profile__project-list" aria-busy="true">
              <OngoingRoleSkeletonList />
            </div>
          </section>
        ) : ongoingRoles.length > 0 ? (
          <section className="my-profile__block">
            <div className="my-profile__block-head">
              <h3 className="my-profile__block-title">Ongoing Role</h3>
            </div>
            <div className="my-profile__project-list">
              {ongoingRoles.map((role) => (
                <div key={role.id} className="my-profile__ongoing-card">
                  <span
                    className={`my-profile__code my-profile__code--${genreSlug(
                      role.genre
                    )}`}
                  >
                    {role.code}
                  </span>
                  <div className="my-profile__ongoing-body">
                    <div className="my-profile__ongoing-left">
                      <b>{role.title}</b>
                      <div className="my-profile__genre-list">
                        {(role.genres || [role.genre]).map(genre => (
                          <GenrePill key={`${role.id}-${genre}`} genre={genre} />
                        ))}
                      </div>
                    </div>
                    <div className="my-profile__ongoing-meta">
                      <span>{role.role}</span>
                      <span className="my-profile__dot" aria-hidden="true">
                        •
                      </span>
                      <span>{role.date}</span>
                      <span className="my-profile__dot" aria-hidden="true">
                        •
                      </span>
                      <span>{role.duration}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </section>
        ) : null}

        {isLoadingProjects
          ? PROJECT_SECTIONS.map((section) => (
              <section key={section.key} className="my-profile__block">
                <div className="my-profile__block-head">
                  <h3 className="my-profile__block-title">{section.title}</h3>
                </div>
                <div
                  className="my-profile__project-list"
                  aria-busy="true"
                >
                  <ProfileProjectSkeletonList showActions={false} />
                </div>
              </section>
            ))
          : PROJECT_SECTIONS.map((section) => {
              const rows = sectionProjects[section.key] || [];
              if (!rows.length) return null;

              return (
                <section key={section.key} className="my-profile__block">
                  <div className="my-profile__block-head">
                    <h3 className="my-profile__block-title">{section.title}</h3>
                  </div>
                  <div className="my-profile__project-list">
                    {rows.map((project) => (
                      <ProjectCard
                        key={project.id || `${section.key}-${project.title}`}
                        project={project}
                      />
                    ))}
                  </div>
                </section>
              );
            })}
      </div>

      <AddCollaboratorModal
        isOpen={isInviteOpen}
        onClose={() => setIsInviteOpen(false)}
        showProjectSelect
        title="Invite to Project"
        subtitle="Select a project this member is not already on."
        excludeMemberUserId={profileId}
        excludeProjectIds={inviteExcludeProjectIds}
        presetCollaborators={invitePresetCollaborators}
        lockCollaborators
      />
    </div>
  );
};

export default PublicProfile;
